import { Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, styled } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import SearchUserComponent from './listFilters/searchUser';
import SelectCompany, { CompanyListObject, getComponyNameById } from './listFilters/selectCompany';

import { TextStyle, customScrollbarStyle } from '../../../../assets/styles/custom.styles';
import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import getErrorMessage from '../../../../assets/utils/getErrorMessage';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import ErrorResponseObject from '../../../../interfaces/errorResponseObject';
import RequestStatusObject from '../../../../interfaces/RequestStatusObject';
import { TenantFromApiObject } from '../../../../Page/customer/partner/customer/customerListPage';
import LoadingComponent from '../../../common/loadingComponent';

export interface RemovedUserObject {
  id: string;
  fullName: string;
  email: string;
  company: string;
  role: string;
  status: string;
}

interface DeletedUserListProps {
  isLoading: boolean;
  isError: boolean;
  errorText: string;
  list: Array<RemovedUserObject>
  searchValue: string;
  setSearchValue: (newValue:string) => void;
  companyId: string;
  setCompanyId: (newValue: string) => void;
  mutate: () => void;
}

const container = {
  border:'1px solid rgba(0, 0, 0, 0.16)',
  borderRadius: '8px',
  maxWidth: '100%',
  maxHeight: 'calc(100vh - 220px)',
};

const tableStyle = {
  maxHeight: 'calc(100vh - 300px)',
  overflowY:'auto',
};

const HeaderCell = styled(TableCell)({
  color: '#878787!important',
  fontSize: '14px',
  fontWeight: 500,
});
const BodyCell = styled(TableCell)({
  color: '#181818',
  fontSize: '14px',
  fontWeight: 500,
});

const ReactivateButtonStyle = {
  color: '#33DA7F',
  minWidth:'120px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '125%',
  textTransform: 'none',
  fontFamily: 'Schibsted Grotesk',
  backgroundColor: '#D5FFE9',
  border: '1px solid #D5FFE9',
  '&:hover': {
    backgroundColor: '#D5FFE9',
    border: '1px solid #009644',
  },
};

const transformDataFromAPI = (data: Array<TenantFromApiObject>): Array<CompanyListObject> => {
  const newList: Array<CompanyListObject> = data?.map(({ id, name, slug }) => ({
    id, 
    name,
    slug,
  }));

  return newList;
};

const RemovedUserList = ({ 
  isLoading, 
  isError, 
  errorText, 
  list, 
  searchValue, 
  setSearchValue, 
  companyId, 
  setCompanyId,
  mutate }: DeletedUserListProps) => {

  const { createToastNotification } = useToastNotificationContext();

  const [companyList, setCompanyList] = useState<CompanyListObject[]>([]);
  const [requestStatus, setRequestStatus] = useState<RequestStatusObject>({
    isLoading: false,
    success: false,
    error: false,
    responseText: '',
  });

  const { data, isLoading: isCompanyLoading, error: isCompanyError  } = useSWR('/confide/tenants', { revalidateOnFocus: false });

  useEffect(()=> {
    setCompanyList(transformDataFromAPI(data));
  }, [data]);

  const ReactivateUser = async (id: string) => {
    if (id) {
      setRequestStatus({
        isLoading: true,
        success: false,
        error: false,
        responseText: '',
      });
      try {
        const response = await axiosInstance().patch(`confide/removed-users/${id}`, { is_active: true });
        if (response.status === StatusCodes.OK) {
          setRequestStatus({
            isLoading: false,
            success: true,
            error: false,
            responseText: '',
          });
          mutate();
          createToastNotification('User Reactivation Successful', 'success');
        }

      } catch (err) {
        const errorMessage = getErrorMessage(err as ErrorResponseObject);
        setRequestStatus({
          isLoading: false,
          success: false,
          error: true,
          responseText: errorMessage,
        });
        createToastNotification(errorMessage, 'error');

      }
    }
  };


  return (
    <Stack component={Paper} sx={container}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography sx={{
          ...TextStyle.headerText, 
          padding: '1.5rem 1.25rem' }}
        >List of all Removed User
        </Typography>
        <Stack direction='row' alignItems='center' justifyContent='center' marginRight='20px' gap={1}>
          <SelectCompany selected={companyId} setSelected={setCompanyId} list={companyList} isLoading={isCompanyLoading} error={isCompanyError}/>
          <SearchUserComponent value={searchValue} onChangeValue={setSearchValue} />
        </Stack>
      </Stack>
      <TableContainer sx={customScrollbarStyle}>
        <Table stickyHeader sx={tableStyle}>
          <TableHead>
            <TableRow>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Company</HeaderCell>
              <HeaderCell>Role</HeaderCell>
              <HeaderCell sx={{ textAlign: 'end' }}>Action</HeaderCell>
            </TableRow>
          </TableHead>
          {!isLoading &&
            <TableBody>
              {Array.isArray(list) && list.length > 0 && 
              list.map(({ id, fullName, email, company, role }, key) => (
                <TableRow key={key}>
                  <BodyCell sx={{ maxWidth:'150px' }}>{fullName}</BodyCell>
                  <BodyCell sx={{  maxWidth:'100px' }}>{email}</BodyCell>
                  <BodyCell sx={{  maxWidth:'100px' }}>{getComponyNameById(company, companyList)}</BodyCell>
                  <BodyCell sx={{ maxWidth:'50px' }}>{role}</BodyCell>
                  <BodyCell sx={{ textAlign: 'end', maxWidth:'10px' }}>
                    <Button
                      sx={ReactivateButtonStyle}
                      onClick = {() => ReactivateUser(id)}>
                      {requestStatus.isLoading ? <CircularProgress size={20}/> : 'Reactivate'}
                    </Button>
                  </BodyCell>
                </TableRow>
              )) 
              }
            </TableBody>
          }
        </Table>
        {
          list.length === 0 && 
          <Typography sx={{
            textAlign: 'center',
            padding:'40px',
          }}>No users found</Typography>
        }
        {isLoading && <LoadingComponent isMinHeight/>}
        {!isLoading && isError &&
          <Typography sx={{
            textAlign: 'center',
            padding:'40px',
            color:'#FF5E56',
          }}>{errorText}</Typography>
        }
      </TableContainer>
    </Stack>
  );
};

export default RemovedUserList;